import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import ZohoLogo from '../../shared/assets/svg/zohoLogo.svg';
import MetaLogo from '../../shared/assets/svg/metaLogo.svg';
import Play from '../../shared/assets/svg/play.svg';
import { getAllCompanies, getBuilderWhatsapp } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import {
  BUILDER_WHATSAPP_QUERY_KEY,
  isBuilderWhatsappConfigured
} from '../utils/builderWhatsappAccess';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const FACEBOOK_OAUTH_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'leads_retrieval',
  'pages_manage_ads',
  'business_management'
].join(',');

const Integration = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const { collapsed } = useSidebar();

  const handleDemoModalToggle = () => {
    setIsDemoModalOpen(!isDemoModalOpen);
  };

  const handleFacebookLogin = () => {
    const appId = '9566903480068710';
    const redirectUri = 'https://bec4-223-185-134-209.ngrok-free.app/facebook/callback';
    const fbLoginUrl = `https://www.facebook.com/v17.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(FACEBOOK_OAUTH_SCOPES)}&response_type=code`;
    window.location.href = fbLoginUrl;
  };

  // Query to get all companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  const {
    data: builderWhatsappData,
    isPending: isWhatsappIntegrationPending,
    isError: isWhatsappIntegrationError
  } = useQuery({
    queryKey: BUILDER_WHATSAPP_QUERY_KEY,
    queryFn: getBuilderWhatsapp
  });

  const isWhatsappConfigured =
    !isWhatsappIntegrationError && isBuilderWhatsappConfigured(builderWhatsappData);
  const whatsappConfigureLabel = isWhatsappConfigured ? 'Configured' : 'Not Configured';

  // Track page view
  useEffect(() => {
    mixpanel.track('Integration Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (companiesData) {
      setCompanies(companiesData);
    }
  }, [companiesData]);

  const handleViewCompanies = () => {
    navigate('/builder/integration/facebook-companies');
  };

  const handleViewMailCompanies = () => {
    navigate('/builder/integration/mail-companies');
  };

  const handleViewZohoCompanies = () => {
    navigate('/builder/integration/zoho-companies');
  };

  return (
    <Layout>
      {/* Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
        <Header data={{ name: 'Integrations' }} />
      </div>
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        {/* Main Content */}
        <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Zoho Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <img src={ZohoLogo} alt="Zoho Logo" className="h-12 w-auto mb-4 object-contain" />
              <h3 className="font-bold text-lg mb-2 flex gap-2">Zoho</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Easily integrate with Zoho to sync your data and streamline your processes. Whether
                its Zoho CRM, Zoho Projects, or other Zoho apps, our integration ensures smooth
                connectivity and helps you work more efficiently without manual data transfers.
              </p>
              <div className="w-full flex gap-4 justify-between mt-auto">
                <Button displayType="secondary" onClick={handleDemoModalToggle}>
                  <span className="flex gap-1 justify-start items-center">
                    <img src={Play} alt="" className="pt-1" />
                    Demo
                  </span>
                </Button>
                <Button onClick={handleViewZohoCompanies}>View Companies</Button>
              </div>
            </div>

            {/* WhatsApp Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-12 h-12"
                  fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">WhatsApp</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Integrate WhatsApp Business API to send notifications, support customers, and run
                campaigns. Connect your phone number and access token to enable messaging and
                streamline communication.
              </p>
              <div className="w-full flex justify-end mt-auto">
                <Button displayType="primary" isLoading={isWhatsappIntegrationPending} disabled={isWhatsappIntegrationPending}>
                  {whatsappConfigureLabel}
                </Button>
              </div>
            </div>

            {/* Meta Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <img src={MetaLogo} alt="Meta" className="h-12 w-auto mb-4 object-contain" />
              <h3 className="font-bold text-lg mb-2 flex gap-2">Meta</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Easily integrate with Meta to sync data and streamline processes. Whether
                it&apos;s Ads, Facebook Pages, or other tools, our integration ensures smooth
                connectivity and efficient work without manual transfers.
              </p>
              <div className="w-full flex gap-4 justify-between mt-auto">
                <Button displayType="secondary" onClick={handleDemoModalToggle}>
                  <span className="flex gap-1 justify-start items-center">
                    <img src={Play} alt="" className="pt-1" />
                    Demo
                  </span>
                </Button>
                <Button onClick={handleViewCompanies}>View Companies</Button>
              </div>
            </div>

            {/* Email Setup Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-12 h-12"
                  fill="#EA4335">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">Email Setup</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Easily integrate with Email to sync data and streamline processes. Whether it&apos;s
                campaigns, notifications, or other tools, our integration ensures smooth connectivity.
              </p>
              <div className="w-full flex gap-4 justify-between mt-auto">
                <Button displayType="secondary" onClick={handleDemoModalToggle}>
                  <span className="flex gap-1 justify-start items-center">
                    <img src={Play} alt="" className="pt-1" />
                    Demo
                  </span>
                </Button>
                <Button onClick={handleViewMailCompanies}>View Companies</Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      {isDemoModalOpen && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name="Zoho Demo"
          onClose={handleDemoModalToggle}
          width={50}>
          <iframe
            width="100%"
            height="315"
            src="https://www.youtube.com/embed/zv0jXPmKBrk"
            title="Zoho Integration Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen></iframe>
        </Modal>
      )}

    </Layout>
  );
};

export default Integration;
